import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Separator } from './ui/separator';
import { useToast } from './ui/use-toast';
import { MessageSquare, Clock, CheckCircle, User, Mail, Bell, Archive, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast as sonnerToast } from 'sonner';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';

interface FeedbackItem {
  id: string;
  title: string;
  description: string;
  feedback_type: 'suggestion' | 'bug' | 'other' | 'question';
  status: 'pending' | 'resolved' | 'in_progress' | 'rejected' | 'archived';
  created_at: string;
  user_id: string | null;
  anonymous_name: string | null;
  anonymous_email: string | null;
  response: string | null;
  user_email?: string;
  user_name?: string;
}

const FeedbackManagementPanel: React.FC = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(null);
  const [response, setResponse] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('active');
  const [deleteTarget, setDeleteTarget] = useState<FeedbackItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchFeedback();
    
    const channel = supabase
      .channel('feedback-management-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'feedback'
        },
        (payload) => {
          console.log('New feedback received in management panel:', payload);
          const newFeedback = payload.new;
          
          sonnerToast.success(t('feedback.newReceived'), {
            description: `${newFeedback.feedback_type}: ${newFeedback.title}`,
          });

          fetchFeedback();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'feedback'
        },
        () => {
          fetchFeedback();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const feedbackWithUsers = await Promise.all(
        (data || []).map(async (item) => {
          if (item.user_id) {
            const { data: userData } = await supabase
              .from('users')
              .select('email, full_name')
              .eq('id', item.user_id)
              .single();
            
            return {
              ...item,
              user_email: userData?.email || item.anonymous_email,
              user_name: userData?.full_name || item.anonymous_name || 'Anonymous User'
            };
          }
          return {
            ...item,
            user_email: item.anonymous_email,
            user_name: item.anonymous_name || 'Anonymous User'
          };
        })
      );

      setFeedback(feedbackWithUsers);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      toast({
        title: t('common.error'),
        description: t('feedback.loadError'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateFeedbackStatus = async (feedbackId: string, status: 'resolved' | 'in_progress' | 'archived', responseText?: string) => {
    try {
      const updateData: any = { status };
      if (responseText) {
        updateData.response = responseText;
      }

      const { error } = await supabase
        .from('feedback')
        .update(updateData)
        .eq('id', feedbackId);

      if (error) throw error;

      const statusKey = status === 'archived' ? t('feedback.archived') : status;
      toast({
        title: t('common.success'),
        description: t('feedback.statusUpdated', { status: statusKey })
      });

      fetchFeedback();
      setSelectedFeedback(null);
      setResponse('');
    } catch (error) {
      console.error('Error updating feedback:', error);
      toast({
        title: t('common.error'),
        description: t('feedback.updateError'),
        variant: "destructive"
      });
    }
  };

  const deleteFeedback = async (feedbackId: string) => {
    try {
      setIsDeleting(true);
      const { error } = await supabase
        .from('feedback')
        .delete()
        .eq('id', feedbackId);

      if (error) throw error;

      toast({
        title: t('common.success'),
        description: t('feedback.feedbackDeleted')
      });

      fetchFeedback();
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error deleting feedback:', error);
      toast({
        title: t('common.error'),
        description: t('feedback.feedbackDeleteError'),
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'in_progress':
        return <MessageSquare className="h-4 w-4" />;
      case 'resolved':
        return <CheckCircle className="h-4 w-4" />;
      case 'archived':
        return <Archive className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'default';
      case 'in_progress':
        return 'secondary';
      case 'resolved':
        return 'outline';
      case 'archived':
        return 'outline';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'bug':
        return 'destructive';
      case 'suggestion':
        return 'default';
      case 'question':
        return 'secondary';
      case 'other':
        return 'outline';
      default:
        return 'outline';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-pulse">{t('feedback.loading')}</div>
      </div>
    );
  }

  const pendingCount = feedback.filter(f => f.status === 'pending').length;

  const filteredFeedback = feedback.filter(f => {
    if (filterStatus === 'active') return f.status !== 'archived';
    if (filterStatus === 'archived') return f.status === 'archived';
    if (filterStatus === 'pending') return f.status === 'pending';
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('feedback.managementTitle')}</h1>
          <p className="text-muted-foreground">{t('feedback.managementSubtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          {pendingCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-destructive/10 rounded-lg">
              <Bell className="h-4 w-4 text-destructive" />
              <span className="text-sm font-medium text-destructive">{t('feedback.needsAttention')}</span>
            </div>
          )}
          <Badge variant={pendingCount > 0 ? 'destructive' : 'secondary'} className="text-lg px-3 py-1">
            {pendingCount} {t('feedback.pending')}
          </Badge>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant={filterStatus === 'active' ? 'default' : 'outline'}
          onClick={() => setFilterStatus('active')}
        >
          {t('feedback.filterActive')}
        </Button>
        <Button
          size="sm"
          variant={filterStatus === 'pending' ? 'default' : 'outline'}
          onClick={() => setFilterStatus('pending')}
        >
          {t('feedback.pending')}
        </Button>
        <Button
          size="sm"
          variant={filterStatus === 'archived' ? 'default' : 'outline'}
          onClick={() => setFilterStatus('archived')}
        >
          {t('feedback.archived')}
        </Button>
        <Button
          size="sm"
          variant={filterStatus === 'all' ? 'default' : 'outline'}
          onClick={() => setFilterStatus('all')}
        >
          {t('feedback.filterAll')}
        </Button>
      </div>

      <div className="grid gap-4">
        {filteredFeedback.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">{t('feedback.noFeedback')}</p>
            </CardContent>
          </Card>
        ) : (
          filteredFeedback.map((item) => (
            <Card key={item.id} className={`hover:shadow-md transition-shadow ${item.status === 'archived' ? 'opacity-60' : ''}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-lg">{item.title}</CardTitle>
                      <Badge variant={getTypeColor(item.feedback_type)}>
                        {item.feedback_type}
                      </Badge>
                      <Badge variant={getStatusColor(item.status)} className="flex items-center gap-1">
                        {getStatusIcon(item.status)}
                        {item.status === 'archived' ? t('feedback.archived') : item.status}
                      </Badge>
                    </div>
                    <CardDescription className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {item.user_name}
                      </span>
                      {item.user_email && (
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {item.user_email}
                        </span>
                      )}
                      <span>{new Date(item.created_at).toLocaleDateString()}</span>
                    </CardDescription>
                  </div>
                  {/* Archive & Delete buttons */}
                  <div className="flex gap-1">
                    {item.status !== 'archived' && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        onClick={() => updateFeedbackStatus(item.id, 'archived')}
                        title={t('feedback.archiveFeedback')}
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => setDeleteTarget(item)}
                      title={t('feedback.deleteFeedback')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm mb-4">{item.description}</p>
                
                {item.response && (
                  <div className="bg-muted p-4 rounded-lg mb-4">
                    <p className="font-semibold text-sm mb-1">{t('feedback.adminResponse')}</p>
                    <p className="text-sm">{item.response}</p>
                  </div>
                )}

                {selectedFeedback?.id === item.id ? (
                  <div className="space-y-4">
                    <Separator />
                    <div>
                      <label className="text-sm font-medium mb-2 block">{t('feedback.addResponse')}</label>
                      <Textarea
                        value={response}
                        onChange={(e) => setResponse(e.target.value)}
                        placeholder={t('feedback.responsePlaceholder')}
                        rows={3}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => updateFeedbackStatus(item.id, 'in_progress', response)}
                      >
                        {t('feedback.markInProgress')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateFeedbackStatus(item.id, 'resolved', response)}
                      >
                        {t('feedback.markResolved')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedFeedback(null);
                          setResponse('');
                        }}
                      >
                        {t('common.cancel')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  item.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedFeedback(item);
                          setResponse(item.response || '');
                        }}
                      >
                        {t('feedback.respond')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => updateFeedbackStatus(item.id, 'in_progress')}
                      >
                        {t('feedback.markInProgressShort')}
                      </Button>
                    </div>
                  )
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Delete confirmation dialog */}
      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteFeedback(deleteTarget.id)}
        title={t('feedback.confirmDeleteFeedbackTitle')}
        description={t('feedback.confirmDeleteFeedback')}
        itemName={deleteTarget?.title || ''}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default FeedbackManagementPanel;